/**
 * Any colour at all: a hue strip and a square of how strong and how light.
 *
 * The one part of the colour picker that works in screen colour rather than
 * ink, because that is how people think about "a bit bluer" - and the reason
 * the picker grew a gamut warning at the same time. The colour chosen here is
 * turned into ink on the way out, once, by the same conversion the hex box
 * uses, so a grey from the square is still black ink alone.
 */

import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

interface Props {
  /** The colour as the screen shows it, #rrggbb. */
  hex: string;
  onPick: ( hex: string ) => void;
}

export function Spectrum( { hex, onPick }: Props ) {
  const square = useRef<HTMLDivElement>( null );
  const shown = hsvOf( hex );

  /*
   * Hue is kept apart from the colour, because a grey has none: drag to the
   * left edge of the square and the colour forgets which hue it came from.
   * Without this the strip would jump back to red every time, and so would the
   * square behind it.
   */
  const [ hue, setHue ] = useState( shown.h );

  useEffect( () => {
    if ( shown.s > 0.02 && shown.v > 0.02 ) setHue( shown.h );
  }, [ hex ] ); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = ( h: number, s: number, v: number ) => onPick( hexOf( h, s, v ) );

  const fromPointer = ( event: PointerEvent<HTMLDivElement> ) => {
    const box = square.current?.getBoundingClientRect();

    if ( ! box ) return;

    const s = clamp( ( event.clientX - box.left ) / box.width );
    const v = 1 - clamp( ( event.clientY - box.top ) / box.height );

    pick( hue, s, v );
  };

  const onKey = ( event: KeyboardEvent<HTMLDivElement> ) => {
    const step = event.shiftKey ? 0.1 : 0.01;
    const moves: Record<string, [ number, number ]> = {
      ArrowLeft: [ -step, 0 ],
      ArrowRight: [ step, 0 ],
      ArrowUp: [ 0, step ],
      ArrowDown: [ 0, -step ],
    };
    const move = moves[ event.key ];

    if ( ! move ) return;

    event.preventDefault();
    pick( hue, clamp( shown.s + move[ 0 ] ), clamp( shown.v + move[ 1 ] ) );
  };

  return (
    <div className="tb-spectrum">
      <div
        ref={ square }
        className="tb-spectrum__square"
        style={ { backgroundColor: `hsl(${ hue } 100% 50%)` } }
        role="slider"
        tabIndex={ 0 }
        aria-label="How strong and how light"
        aria-valuetext={ `${ Math.round( shown.s * 100 ) }% strong, ${ Math.round( shown.v * 100 ) }% light` }
        onPointerDown={ ( event ) => {
          event.currentTarget.setPointerCapture( event.pointerId );
          fromPointer( event );
        } }
        onPointerMove={ ( event ) => {
          if ( event.currentTarget.hasPointerCapture( event.pointerId ) ) fromPointer( event );
        } }
        onKeyDown={ onKey }
      >
        <span
          className="tb-spectrum__mark"
          style={ { left: `${ shown.s * 100 }%`, top: `${ ( 1 - shown.v ) * 100 }%`, background: hex } }
        />
      </div>

      <input
        className="tb-spectrum__hue"
        type="range"
        min={ 0 }
        max={ 359 }
        step={ 1 }
        value={ Math.round( hue ) }
        aria-label="Hue"
        onChange={ ( event ) => {
          const h = parseFloat( event.target.value );

          setHue( h );
          // A grey has no hue to change, so moving the strip on one picks the
          // hue at full strength - otherwise the strip would seem to do nothing.
          pick( h, shown.s < 0.02 ? 1 : shown.s, shown.v < 0.02 ? 1 : shown.v );
        } }
      />
    </div>
  );
}

/* ---------------------------------------------------------------
 * Hue, saturation, value
 * ------------------------------------------------------------ */

const clamp = ( v: number ) => Math.min( 1, Math.max( 0, v ) );

export function hsvOf( hex: string ): { h: number; s: number; v: number } {
  const clean = hex.replace( /^#/, '' );
  const r = parseInt( clean.slice( 0, 2 ), 16 ) / 255 || 0;
  const g = parseInt( clean.slice( 2, 4 ), 16 ) / 255 || 0;
  const b = parseInt( clean.slice( 4, 6 ), 16 ) / 255 || 0;

  const max = Math.max( r, g, b );
  const min = Math.min( r, g, b );
  const d = max - min;

  let h = 0;

  if ( d > 0 ) {
    if ( max === r ) h = ( ( g - b ) / d ) % 6;
    else if ( max === g ) h = ( b - r ) / d + 2;
    else h = ( r - g ) / d + 4;

    h *= 60;

    if ( h < 0 ) h += 360;
  }

  return { h, s: max === 0 ? 0 : d / max, v: max };
}

export function hexOf( h: number, s: number, v: number ): string {
  const c = v * s;
  const x = c * ( 1 - Math.abs( ( ( h / 60 ) % 2 ) - 1 ) );
  const m = v - c;

  const [ r, g, b ] =
    h < 60 ? [ c, x, 0 ] : h < 120 ? [ x, c, 0 ] : h < 180 ? [ 0, c, x ] : h < 240 ? [ 0, x, c ] : h < 300 ? [ x, 0, c ] : [ c, 0, x ];

  const pair = ( value: number ) => Math.round( ( value + m ) * 255 ).toString( 16 ).padStart( 2, '0' );

  return `#${ pair( r ) }${ pair( g ) }${ pair( b ) }`;
}
